
  import { defineComponent, PropType } from 'vue';
  import useVuelidate from '@vuelidate/core';
  import { required } from '@vuelidate/validators';

  import ComponentInput from '@/common/components/ComponentInput.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import InputBlock from '@/common/components/redesigned/InputBlock.vue';
  import TreeMultiselect from '@/common/components/redesigned/TreeMultiselect/TreeMultiselect.vue';
  import { MenuModes } from '@/common/components/redesigned/TreeMultiselect/types';

  import ModalComponent from '@/components/modals/ModalComponent.vue';

  import { TCustomsOrgansOnBorderItem } from '../../../types/common-aggregated';

  export default defineComponent({
    name: 'AddEditTreeGroupModal',
    components: {
      ModalComponent,
      ComponentInput,
      ButtonComponent,
      InputBlock,
      TreeMultiselect,
    },
    props: {
      show: { type: Boolean, default: false },
      group: { type: Object as PropType<TCustomsOrgansOnBorderItem | null>, default: null },
      groupsTree: {
        type: Array as PropType<TCustomsOrgansOnBorderItem[]>,
        required: true,
      },
    },
    emits: ['save', 'close', 'update:modelValue'],
    data(): {
      form: {
        id: number | null;
        name: string;
        kod: string;
        parent: number | null;
      };
    } {
      return {
        form: {
          id: null,
          name: '',
          kod: '',
          parent: null,
        },
      };
    },
    computed: {
      internalShow: {
        get(): boolean {
          return this.show;
        },
        set(val: boolean) {
          this.$emit('update:modelValue', val);
          if (!val) this.$emit('close');
        },
      },
      isEdit(): boolean {
        return !!this.group;
      },
      parentModel: {
        get(): number[] {
          return this.form.parent !== null ? [this.form.parent] : [];
        },
        set(val: number[]) {
          if (val.length > 0) {
            this.form.parent = Number(val[val.length - 1]);
          } else {
            this.form.parent = null;
          }
        },
      },
      parentCustomValidation() {
        return {
          isValidParent: (value: number[]) => {
            if (value.length === 0) return true;
            const selectedId = value[value.length - 1];
            return this.validateParent(selectedId);
          },
        };
      },
      parentValidationMessage(): string {
        if (this.parentModel.length === 0) {
          return '';
        }
        const selectedId = this.parentModel[this.parentModel.length - 1];
        if (this.isEdit) {
          if (selectedId === this.form.id) {
            return 'Запрещено устанавливать в качестве родительской эту же группу';
          }
          if (this.isDescendant(selectedId, this.form.id!, this.groupsTree)) {
            return 'Нельзя сделать группу дочерней для своего потомка';
          }
        }
        return '';
      },
      menuModes(): typeof MenuModes {
        return MenuModes;
      },
    },

    validations() {
      return {
        form: {
          name: { required },
          parent: {
            isValidParent: (value: number | null) => this.validateParent(value),
          },
        },
      };
    },

    setup() {
      return { v$: useVuelidate({ $stopPropagation: true }) };
    },

    watch: {
      group: {
        handler(newGroup) {
          if (newGroup) {
            this.form = {
              id: newGroup.id,
              name: newGroup.name ?? '',
              kod: newGroup.kod ?? '',
              parent: newGroup.parent,
            };
          } else {
            this.resetForm();
          }
        },
        immediate: true,
      },
      show: {
        handler(newVal) {
          if (newVal) {
            this.$nextTick(() => {
              this.v$.$reset();
            });
          }
        },
        immediate: true,
      },
      'form.parent': {
        handler() {
          this.v$.form.parent.$touch();
        },
      },
    },

    methods: {
      validateParent(value: number | null): boolean {
        if (value === null) return true;
        if (this.isEdit) {
          if (value === this.form.id) return false;
          if (this.isDescendant(value, this.form.id!, this.groupsTree)) return false;
        }
        return true;
      },

      resetForm() {
        this.form = { id: null, name: '', kod: '', parent: null };
      },

      isDescendant(descendantId: number, parentId: number, tree: TCustomsOrgansOnBorderItem[]): boolean {
        if (descendantId === parentId) return true;

        const findNode = (nodes: TCustomsOrgansOnBorderItem[]): TCustomsOrgansOnBorderItem | null => {
          for (const node of nodes) {
            if (node.id === parentId) return node;
            if (node.children) {
              const found = findNode(node.children);
              if (found) return found;
            }
          }
          return null;
        };

        const parentNode = findNode(tree);
        if (!parentNode) return false;

        const searchDescendant = (node: TCustomsOrgansOnBorderItem): boolean => {
          if (node.id === descendantId) return true;
          if (node.children) {
            return node.children.some((child) => searchDescendant(child));
          }
          return false;
        };

        return searchDescendant(parentNode);
      },

      handleSave() {
        this.v$.$touch();
        if (this.v$.$invalid) return;

        this.$emit('save', {
          id: this.form.id,
          name: this.form.name.trim(),
          kod: this.form.kod.trim(),
          parent: this.form.parent,
        });
        this.onClose();
      },

      onClose() {
        this.resetForm();
        this.v$.$reset();
        this.internalShow = false;
      },
    },
  });
